import React, { useState } from 'react';
import { Truck, Container, Camera, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { formatCurrency, formatPercentage, formatNumber } from '../lib/formatters';
import { SparklineChart } from './SparklineChart';
import { PhotoGalleryModal } from './PhotoGalleryModal';
import { useNavigate } from 'react-router-dom';

// Color scheme for ownership breakdown
const COLORS = {
  owned: '#22C55E',      // Green - Your shares
  onSale: '#F59E0B',     // Amber - On sale in marketplace
  otherOwned: '#3B82F6', // Blue - Other owners
  available: '#6B7280',  // Gray - Available to buy
};

export const AssetCard = ({ asset, userShares = 0, onSaleShares = 0 }) => {
  const navigate = useNavigate();
  const [showPhotos, setShowPhotos] = useState(false);
  const Icon = asset.type === 'truck' ? Truck : Container;
  
  // Calculate ownership breakdown
  const totalShares = asset.total_shares;
  const soldShares = asset.sold_shares;
  const availableShares = totalShares - soldShares;
  const otherOwnedShares = Math.max(0, soldShares - userShares - onSaleShares);
  
  // Percentages
  const yourPercent = (userShares / totalShares) * 100;
  const onSalePercent = (onSaleShares / totalShares) * 100;
  const otherOwnedPercent = (otherOwnedShares / totalShares) * 100;
  const availablePercent = (availableShares / totalShares) * 100;

  // Price trend
  const priceTrend = asset.price_trend || 'stable';
  const priceChangePercent = asset.price_change_percent || 0;
  const recentPerf = asset.recent_performance;

  // Trend icon and color
  const TrendIcon = priceTrend === 'up' ? TrendingUp : priceTrend === 'down' ? TrendingDown : Minus;
  const trendColor = priceTrend === 'up' ? 'text-green-500' : priceTrend === 'down' ? 'text-red-500' : 'text-gray-400';
  const trendBg = priceTrend === 'up' ? 'bg-green-500/10' : priceTrend === 'down' ? 'bg-red-500/10' : 'bg-gray-500/10';

  return (
    <>
      <div
        data-testid={`asset-card-${asset.asset_id}`}
        className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-lg hover:border-[hsl(var(--border-strong))] transition-all duration-200"
      >
        {/* Vector Graphic - Main Visual */}
        <div 
          className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 cursor-pointer" 
          onClick={() => navigate(`/asset/${asset.asset_id}`)}
        >
          <svg viewBox="0 0 400 160" className="w-full h-auto">
            <defs>
              <linearGradient id={`fill-${asset.asset_id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={COLORS.owned} />
                <stop offset={`${yourPercent}%`} stopColor={COLORS.owned} />
                <stop offset={`${yourPercent}%`} stopColor={COLORS.onSale} />
                <stop offset={`${yourPercent + onSalePercent}%`} stopColor={COLORS.onSale} />
                <stop offset={`${yourPercent + onSalePercent}%`} stopColor={COLORS.otherOwned} />
                <stop offset={`${yourPercent + onSalePercent + otherOwnedPercent}%`} stopColor={COLORS.otherOwned} />
                <stop offset={`${yourPercent + onSalePercent + otherOwnedPercent}%`} stopColor={COLORS.available} />
                <stop offset="100%" stopColor={COLORS.available} />
              </linearGradient>
            </defs>

            {asset.type === 'truck' ? (
              <g>
                {/* Truck Cab */}
                <path
                  d="M 30 75 L 30 55 L 50 55 L 50 45 L 90 45 L 100 35 L 115 35 L 120 45 L 120 75"
                  fill={`url(#fill-${asset.asset_id})`}
                  stroke="#1F2937"
                  strokeWidth="2"
                />
                <rect x="85" y="40" width="30" height="18" fill="#60A5FA" opacity="0.6" stroke="#1F2937" strokeWidth="1" rx="1" />
                
                {/* Trailer */}
                <rect
                  x="125"
                  y="45"
                  width="245"
                  height="32"
                  fill={`url(#fill-${asset.asset_id})`}
                  stroke="#1F2937"
                  strokeWidth="2"
                  rx="2"
                />
                
                {/* Panel lines */}
                <line x1="200" y1="47" x2="200" y2="75" stroke="#1F2937" strokeWidth="1" opacity="0.3" />
                <line x1="275" y1="47" x2="275" y2="75" stroke="#1F2937" strokeWidth="1" opacity="0.3" />
                
                {/* Wheels */}
                <circle cx="65" cy="82" r="10" fill="#1F2937" stroke="#374151" strokeWidth="2" />
                <circle cx="65" cy="82" r="5" fill="#6B7280" />
                <circle cx="105" cy="82" r="10" fill="#1F2937" stroke="#374151" strokeWidth="2" />
                <circle cx="105" cy="82" r="5" fill="#6B7280" />
                <circle cx="330" cy="82" r="10" fill="#1F2937" stroke="#374151" strokeWidth="2" />
                <circle cx="330" cy="82" r="5" fill="#6B7280" />
                <circle cx="355" cy="82" r="10" fill="#1F2937" stroke="#374151" strokeWidth="2" />
                <circle cx="355" cy="82" r="5" fill="#6B7280" />
              </g>
            ) : (
              <g>
                {/* Trailer Box */}
                <rect
                  x="40"
                  y="40"
                  width="320"
                  height="45"
                  fill={`url(#fill-${asset.asset_id})`}
                  stroke="#1F2937"
                  strokeWidth="2"
                  rx="2"
                />
                
                {/* Panel lines */}
                <line x1="120" y1="42" x2="120" y2="83" stroke="#1F2937" strokeWidth="1" opacity="0.3" />
                <line x1="200" y1="42" x2="200" y2="83" stroke="#1F2937" strokeWidth="1" opacity="0.3" />
                <line x1="280" y1="42" x2="280" y2="83" stroke="#1F2937" strokeWidth="1" opacity="0.3" />
                
                {/* Landing gear */}
                <rect x="70" y="85" width="5" height="25" fill="#4B5563" stroke="#1F2937" strokeWidth="1" />
                <rect x="95" y="85" width="5" height="25" fill="#4B5563" stroke="#1F2937" strokeWidth="1" />
                
                {/* Wheels - 2 tandem */}
                <circle cx="300" cy="92" r="12" fill="#1F2937" stroke="#374151" strokeWidth="2" />
                <circle cx="300" cy="92" r="6" fill="#6B7280" />
                <circle cx="330" cy="92" r="12" fill="#1F2937" stroke="#374151" strokeWidth="2" />
                <circle cx="330" cy="92" r="6" fill="#6B7280" />
              </g>
            )}
          </svg>
          
          {/* Ownership Breakdown Mini Legend */}
          <div className="grid grid-cols-4 gap-1 mt-3">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.owned }} />
                <span className="text-[10px] text-gray-400">YOU</span>
              </div>
              <div className="text-sm font-bold text-green-400 font-mono">{formatNumber(userShares)}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.onSale }} />
                <span className="text-[10px] text-gray-400">SALE</span>
              </div>
              <div className="text-sm font-bold text-amber-400 font-mono">{formatNumber(onSaleShares)}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.otherOwned }} />
                <span className="text-[10px] text-gray-400">SOLD</span>
              </div>
              <div className="text-sm font-bold text-blue-400 font-mono">{formatNumber(otherOwnedShares)}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.available }} />
                <span className="text-[10px] text-gray-400">AVAIL</span>
              </div>
              <div className="text-sm font-bold text-gray-300 font-mono">{formatNumber(availableShares)}</div>
            </div>
          </div>
          
          {/* Progress Bar showing ownership breakdown */}
          <div className="h-3 bg-gray-700 rounded-full overflow-hidden flex mt-2 border border-gray-600">
            {yourPercent > 0 && (
              <div style={{ width: `${yourPercent}%`, backgroundColor: COLORS.owned }} className="h-full transition-all" />
            )}
            {onSalePercent > 0 && (
              <div style={{ width: `${onSalePercent}%`, backgroundColor: COLORS.onSale }} className="h-full transition-all" />
            )}
            {otherOwnedPercent > 0 && (
              <div style={{ width: `${otherOwnedPercent}%`, backgroundColor: COLORS.otherOwned }} className="h-full transition-all" />
            )}
            {availablePercent > 0 && (
              <div style={{ width: `${availablePercent}%`, backgroundColor: COLORS.available }} className="h-full transition-all" />
            )}
          </div>
        </div>

        {/* Card Content */}
        <div className="p-4">
          {/* View Real Photos Button - Always visible */}
          <button
            data-testid={`view-photos-${asset.asset_id}`}
            onClick={(e) => {
              e.stopPropagation();
              if (asset.image_url) {
                setShowPhotos(true);
              } else {
                navigate(`/asset/${asset.asset_id}`);
              }
            }}
            className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm"
          >
            <Camera className="w-4 h-4" />
            View Real Photos
          </button>

          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-[hsl(var(--background-secondary))] rounded-lg">
                <Icon className="w-4 h-4 text-[hsl(var(--brand-primary))]" />
              </div>
              <div>
                <h3 className="font-semibold text-[hsl(var(--text-primary))] text-base leading-tight">
                  {asset.name}
                </h3>
                <p className="text-xs font-mono text-[hsl(var(--text-tertiary))]">
                  #{asset.unit_number}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold font-mono text-[hsl(var(--text-primary))]">
                {formatCurrency(asset.current_price)}
              </div>
              <div className="flex items-center justify-end gap-1">
                <TrendIcon className={`w-3 h-3 ${trendColor}`} />
                <span className={`text-xs font-semibold ${trendColor}`}>
                  {priceChangePercent > 0 ? '+' : ''}{priceChangePercent.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Daily Performance Indicator */}
          {recentPerf && (
            <div className={`mb-3 p-2 rounded-lg ${recentPerf.performance_trend === 'positive' ? 'bg-green-50 border border-green-200' : recentPerf.performance_trend === 'negative' ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Last {recentPerf.days} days</span>
                <span className={`text-xs font-bold ${recentPerf.performance_trend === 'positive' ? 'text-green-600' : recentPerf.performance_trend === 'negative' ? 'text-red-600' : 'text-gray-600'}`}>
                  {recentPerf.avg_daily_profit >= 0 ? '+' : ''}{formatCurrency(recentPerf.avg_daily_profit)}/day
                </span>
              </div>
            </div>
          )}
          {/* Sparkline */}
          <div className="mb-3">
            <SparklineChart assetId={asset.asset_id} />
          </div>

          {/* Status Badge & Reserve Button */}
          <div className="flex items-center justify-between">
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                asset.status === 'available'
                  ? 'bg-[hsl(var(--gain-light))] text-[hsl(var(--gain))]'
                  : asset.status === 'sold_out'
                  ? 'bg-[hsl(var(--loss-light))] text-[hsl(var(--loss))]'
                  : 'bg-[hsl(var(--background-secondary))] text-[hsl(var(--text-secondary))]'
              }`}
            >
              {asset.status.replace('_', ' ').toUpperCase()}
            </span>
            {asset.allow_buy_requests && asset.status === 'available' && (
              <button
                data-testid={`reserve-button-${asset.asset_id}`}
                className="text-sm font-semibold text-[hsl(var(--brand-primary))] hover:text-[hsl(var(--brand-primary-dark))] transition-colors duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/asset/${asset.asset_id}`);
                }}
              >
                Reserve →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        images={asset.image_url ? [asset.image_url] : []}
        assetName={asset.name}
        isOpen={showPhotos}
        onClose={() => setShowPhotos(false)}
      />
    </>
  );
};
